import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useGetGuestJourneySettingsQuery } from 'api/client-domain/client-domain';
import {
  getGuestJourneyStageTagValueLabels,
  isGuestJourneyStageTag,
} from 'api/reports-service/guest-journey-settings.utils';
import {
  useGetCustomerQuery,
  useListCustomerTagsQuery,
} from 'api/user-domain/user-domain';
import {
  CustomerTag,
  GetCustomerTagsResult,
} from 'api/user-domain/user-domain.types';
import { useMemo } from 'react';
import partition from 'lodash.partition';

/**
 * `displayValue` is the friendly label to render in place of the raw `value`
 * (set only for GJ V2 stage tags, whose stored value is a numeric tier); it is
 * undefined for every other tag, so display surfaces fall back to `value`.
 */
export type CustomersTag = CustomerTag & {
  value: string;
  displayValue?: string;
};

export type UseCustomersTagsReturnType = {
  isLoading: boolean;
  customersTags: CustomersTag[];
  tags: CustomerTag[];
  customerExtendedAttributeTags: CustomersTag[];
  userSpecialTags: CustomersTag[];
};

export const useCustomersTags = (
  customerId: string,
): UseCustomersTagsReturnType => {
  const { data: customerData, isLoading: isCustomerLoading } =
    useGetCustomerQuery(customerId ? customerId : skipToken);
  const { data: tags, isLoading: isTagsLoading } = useListCustomerTagsQuery(
    customerId
      ? {
          count: 1000,
          page: 0,
        }
      : skipToken,
  );

  // GJ V2 stage tags store a numeric tier; we display the client's configured
  // stage name. Only fetch the GJ settings when this customer actually carries
  // such a tag, so ordinary customer profiles don't pull it.
  const hasStageTag = useMemo(
    () =>
      !!customerData &&
      Object.keys(customerData.extendedAttributes).some(isGuestJourneyStageTag),
    [customerData],
  );
  const { data: guestJourneySettings } = useGetGuestJourneySettingsQuery(
    hasStageTag ? undefined : skipToken,
  );

  const customersTags = useMemo(() => {
    if (customerData && tags) {
      return Object.entries(customerData.extendedAttributes)
        .reduce((acc, [key, value]) => {
          const tag = tags.tagAndRuleDTOS.find(tag => tag.key === key);
          if (tag) {
            acc.push({ ...tag, value });
          }
          return acc;
        }, [])
        .filter(tag => tag.source === 'IMPORTED_MANUAL')
        .sort((a, b) => {
          const aUpper = a.title.toUpperCase();
          const bUpper = b.title.toUpperCase();
          if (aUpper < bUpper) {
            return -1;
          }

          return 1;
        });
    } else {
      return null;
    }
  }, [customerData, tags]);

  const filteredTags = useMemo(
    () => tags?.tagAndRuleDTOS?.filter(tag => tag.source === 'IMPORTED_MANUAL'),
    [tags?.tagAndRuleDTOS],
  );

  const customerExtendedAttributeTags: CustomersTag[] = useMemo(() => {
    if (!customerData || !tags) return [];
    return Object.entries(customerData.extendedAttributes).reduce(
      (acc, [key, value]) => {
        const tag = tags.tagAndRuleDTOS.find(tag => tag.key === key);
        if (tag) {
          const valueLabels = getGuestJourneyStageTagValueLabels(
            key,
            guestJourneySettings,
          );
          acc.push({
            ...tag,
            value,
            // Friendly label for GJ V2 stage tiers; the raw value otherwise.
            displayValue: valueLabels?.[String(value)] ?? value,
          });
        }
        return acc;
      },
      [],
    );
  }, [customerData, tags, guestJourneySettings]);

  const userSpecialTags = useMemo(() => {
    const specialTagsArray = [];
    if (customerData && tags) {
      const extendedAttributeKeys = Object.keys(
        customerData?.extendedAttributes,
      );

      const hiddenAttributes = [
        'AVERAGE_PURCHASES_PER_WEEK',
        'AVERAGE_BASKET_VALUE_PER_MONTH',
        'LOYALTY_POINT',
        'LAST_PURCHAS_ON',
        'QUANTILE_AVERAGE_BASKET_VALUE',
        'QUANTILE_DAYS_SINCE_LAST_PURCHASE',
        'QUANTILE_PURCHASES_PER_MONTH',
        'EXT_LOYALTY_ID',
        'DAYS_SINCE_LAST_PURCHASE',
        'AVERAGE_BASKET_VALUE',
        'BASKET_VALUE_TOTAL',
        'BASKET_SUBTOTAL',
        'PURCHASES_PER_MONTH',
        'ORDER_TYPE',
        'ORDER_OPTION_TYPE',
        'NO_OF_TRANSACTIONS',
        'USER_ACCOUNT_NUMBER',
      ];

      extendedAttributeKeys.forEach(key => {
        if (!hiddenAttributes.includes(key)) {
          const tag = tags.tagAndRuleDTOS.find(tag => tag.key === key);
          if (tag === undefined) {
            const TagWithValue = {
              id: key,
              key: key,
              value: customerData?.extendedAttributes[key],
            };
            specialTagsArray.push(TagWithValue);
          }
        }
      });
    }
    return specialTagsArray;
  }, [customerData, tags]);

  return {
    isLoading: isCustomerLoading || isTagsLoading,
    customersTags, // These are the customer's extended attributes merged with additional tag data
    tags: filteredTags, // These are just the results of list customer tags, but filtered
    customerExtendedAttributeTags,
    userSpecialTags: userSpecialTags,
  };
};

export const useCustomerTags = () =>
  useListCustomerTagsQuery({
    count: 1000,
    page: 0,
  });

export const useOfferCustomerTagsAndCustomTargetingTags = () => {
  const { data } = useCustomerTags();

  return useMemo(() => {
    if (!data) return [[], []];

    const sortedTags = data.tagAndRuleDTOS;
    return partition(sortedTags, tag => tag.source !== 'SYSTEM');
  }, [data]);
};

export const useManualTags = () => {
  const { data } = useCustomerTags();

  return useMemo(() => {
    if (!data) return [];
    else {
      return filterTags(data, 'IMPORTED_MANUAL');
    }
  }, [data]);
};

export const useSystemTags = () => {
  const { data } = useCustomerTags();

  return useMemo(() => {
    if (!data) return [];
    else {
      return filterTags(data, 'SYSTEM');
    }
  }, [data]);
};

const segmentCustomTagKeys = [
  'AVERAGE_BASKET_VALUE',
  'AVERAGE_BASKET_VALUE_PER_MONTH',
  'PURCHASES_PER_MONTH',
  'AVERAGE_PURCHASES_PER_WEEK',
  'AVERAGE_WEEKLY_SPEND',
  'BASKET_VALUE_TOTAL',
  'NO_OF_TRANSACTIONS',
  'LAST_PURCHAS_ON',
  'DAYS_SINCE_LAST_PURCHASE',
  'ATTRITION_RISK',
  'JOURNEY_STAGE',
  'REFERRAL',
  'POINTS',
];

// Use this for segments instead of useSystemTags
export const useSegmentCustomTags = () => {
  const systemTags = useSystemTags();

  return useMemo(
    () => systemTags.filter(tag => segmentCustomTagKeys.includes(tag.key)),
    [systemTags],
  );
};

export const filterTags = (
  data: GetCustomerTagsResult | undefined,
  source: CustomerTag['source'],
): CustomerTag[] | undefined =>
  data?.tagAndRuleDTOS
    .filter(tag => tag.source === source)
    .sort(sortCustomTags);

export const sortCustomTags = (a: CustomerTag, b: CustomerTag) => {
  const aUpper = a.title.toUpperCase();
  const bUpper = b.title.toUpperCase();
  if (aUpper < bUpper) return -1;

  return 1;
};
